import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
/**
 * "Жетоны" modal — opened from the UserControls dropdown on a profile (see
 * addArenaUserExtensions.tsx), gated by arena.manageTokens. One combined
 * modal rather than OverrideHistoryModal's separate history popup: a
 * non-negative amount field plus explicit "grant"/"deduct" buttons (mirrors
 * ramon/point-system's AwardPointsModal), plus the adjustment history right
 * below it, same table shape as OverrideHistoryModal but for ArenaTokenLog
 * instead of ArenaOverrideLog.
 */
export default class TokenAdjustModal extends Modal {
  amount = 0;
  reason = '';
  // Track which button is mid-request so only it spins, not both.
  saving = null;
  loadingLog = true;
  entries = [];
  balance = 0;
  className() {
    return 'TokenAdjustModal Modal--medium';
  }
  title() {
    return m('[', null, m("i", {
      className: "fas fa-coins"
    }), ' ', app.translator.trans('forumaker-arena.forum.token_adjust.title', {
      username: this.attrs.user.displayName()
    }));
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.balance = this.attrs.user.attribute('arenaStats')?.tokens ?? 0;
    this.loadLog();
  }
  async loadLog() {
    this.loadingLog = true;
    m.redraw();
    try {
      const resp = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/arena/users/${this.attrs.user.id()}/token-log`
      });
      this.entries = resp.data ?? [];
    } catch {
      // fail silently — same as OverrideHistoryModal
    } finally {
      this.loadingLog = false;
      m.redraw();
    }
  }
  async submit(direction) {
    if (!this.amount) return;
    const amount = direction === 'grant' ? this.amount : -this.amount;
    this.saving = direction;
    m.redraw();
    try {
      const response = await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/arena/users/${this.attrs.user.id()}/tokens`,
        body: {
          amount,
          reason: this.reason.trim() || undefined
        },
        errorHandler: () => {} // see ArenaProfilePage.tsx saveDeck()
      });
      this.balance = response.tokens;
      this.amount = 0;
      this.reason = '';
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('forumaker-arena.forum.token_adjust.saved'));
      this.loadLog();
    } catch (e) {
      const body = e?.response ?? {};
      const message = body?.error ?? app.translator.trans('forumaker-arena.forum.token_adjust.error');
      app.alerts.show({
        type: 'error'
      }, message);
    } finally {
      this.saving = null;
      m.redraw();
    }
  }
  formatDate(iso) {
    const utcStr = iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z';
    const d = new Date(utcStr);
    return d.toLocaleString(app.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  content() {
    return m("div", {
      className: "Modal-body TokenAdjustModal-body"
    }, m("div", {
      className: "TokenAdjustModal-balance"
    }, m("i", {
      className: "fas fa-coins"
    }), app.translator.trans('forumaker-arena.forum.token_adjust.balance', {
      tokens: this.balance
    })), m("div", {
      className: "TokenAdjustModal-form"
    }, m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('forumaker-arena.forum.token_adjust.amount_label')), m("input", {
      className: "FormControl",
      type: "number",
      min: "0",
      step: "1",
      value: this.amount,
      oninput: e => {
        this.amount = Math.max(0, Math.trunc(Number(e.target.value)) || 0);
      }
    })), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('forumaker-arena.forum.token_adjust.reason_label')), m("input", {
      className: "FormControl",
      type: "text",
      maxLength: 255,
      placeholder: app.translator.trans('forumaker-arena.forum.token_adjust.reason_placeholder'),
      value: this.reason,
      oninput: e => {
        this.reason = e.target.value;
      }
    })), m("div", {
      className: "Form-group TokenAdjustModal-submitRow"
    }, m(Button, {
      className: "Button Button--primary",
      loading: this.saving === 'grant',
      disabled: !this.amount || this.saving !== null,
      onclick: () => this.submit('grant')
    }, m("i", {
      className: "fas fa-plus"
    }), " ", app.translator.trans('forumaker-arena.forum.token_adjust.submit_grant')), m(Button, {
      className: "Button Button--danger",
      loading: this.saving === 'deduct',
      disabled: !this.amount || this.saving !== null,
      onclick: () => this.submit('deduct')
    }, m("i", {
      className: "fas fa-minus"
    }), " ", app.translator.trans('forumaker-arena.forum.token_adjust.submit_deduct')))), m("h3", {
      className: "TokenAdjustModal-historyTitle"
    }, m("i", {
      className: "fas fa-history"
    }), " ", app.translator.trans('forumaker-arena.forum.token_adjust.history_title')), this.loadingLog ? m(LoadingIndicator, null) : this.entries.length === 0 ? m("div", {
      className: "TokenAdjustModal-empty"
    }, m("i", {
      className: "fas fa-inbox"
    }), " ", app.translator.trans('forumaker-arena.forum.token_adjust.history_empty')) : m("table", {
      className: "TokenAdjustModal-table"
    }, m("thead", null, m("tr", null, m("th", null, app.translator.trans('forumaker-arena.forum.token_adjust.col_date')), m("th", null, app.translator.trans('forumaker-arena.forum.token_adjust.col_delta')), m("th", null, app.translator.trans('forumaker-arena.forum.token_adjust.col_reason')), m("th", null, app.translator.trans('forumaker-arena.forum.token_adjust.col_balance')), m("th", null, app.translator.trans('forumaker-arena.forum.token_adjust.col_actor')))), m("tbody", null, this.entries.map(e => m("tr", {
      key: e.id
    }, m("td", {
      className: "TokenAdjustModal-date"
    }, this.formatDate(e.createdAt)), m("td", {
      className: e.delta >= 0 ? 'TokenAdjustModal-deltaPositive' : 'TokenAdjustModal-deltaNegative'
    }, e.delta >= 0 ? `+${e.delta}` : e.delta), m("td", null, e.reason || '—'), m("td", null, e.balanceAfter), m("td", null, e.actorName))))));
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/TokenAdjustModal', TokenAdjustModal);